/**
 * What each language is called, and how each vendor wants it spelled.
 *
 * Shared rather than owned by the backend because it is a NAMING table, and
 * both planes need to name a language: the builder offers them, the agents
 * list shows them on a chip. What is deliberately NOT here is the derivation
 * of `sttLanguage` and `locale` from a set of choices — that lives in
 * `agents.languages.ts` on the server and is computed in exactly one place,
 * because two copies of "which tag does synthesis get" is how an agent ends up
 * listening in one language and speaking another.
 *
 * Being in this table does not mean a language can carry an agent. That is a
 * capability question — which legs serve it from inside India — and
 * `offeredLanguages()` answers it against the vendor catalogue.
 */
/**
 * Every language this platform has a name and a tag for.
 *
 * Being here does NOT mean a language can carry an agent: the catalogue
 * decides that. The builder offers every language ANY published model serves
 * (`offeredLanguages()` in `agents.lanes.ts`), and filters each leg's models as
 * languages are chosen. This is the naming table and nothing else — a new
 * vendor changes what we can serve, and nothing changes what Marathi is called.
 *
 * **It is not a product scope.** It used to be Sarvam's 11 (10 Indian plus
 * English), on the reading that "India-only" meant Indian languages. India-only
 * is about where a vendor's traffic is SERVED FROM, not which languages it
 * carries — Soniox's India deployment accepts French. A language missing here
 * cannot be offered however many vendors serve it, so this list has to be at
 * least the union of what the vendors claim.
 *
 * Order is the menu's order: the eleven Sarvam languages first (what most
 * callers on this platform speak), then the other South Asian languages, then
 * everything else alphabetically by English name.
 */
export const LANGUAGES = [
    { code: "en", label: "English", nativeLabel: "English", ttsTag: "en-IN" },
    { code: "hi", label: "Hindi", nativeLabel: "हिन्दी", ttsTag: "hi-IN" },
    { code: "bn", label: "Bengali", nativeLabel: "বাংলা", ttsTag: "bn-IN" },
    { code: "ta", label: "Tamil", nativeLabel: "தமிழ்", ttsTag: "ta-IN" },
    { code: "te", label: "Telugu", nativeLabel: "తెలుగు", ttsTag: "te-IN" },
    { code: "kn", label: "Kannada", nativeLabel: "ಕನ್ನಡ", ttsTag: "kn-IN" },
    { code: "ml", label: "Malayalam", nativeLabel: "മലയാളം", ttsTag: "ml-IN" },
    { code: "mr", label: "Marathi", nativeLabel: "मराठी", ttsTag: "mr-IN" },
    { code: "gu", label: "Gujarati", nativeLabel: "ગુજરાતી", ttsTag: "gu-IN" },
    { code: "pa", label: "Punjabi", nativeLabel: "ਪੰਜਾਬੀ", ttsTag: "pa-IN" },
    // Sarvam spells Odia `od-IN` rather than the ISO `or-IN`. Their spelling,
    // because it is their API that rejects the other one.
    { code: "or", label: "Odia", nativeLabel: "ଓଡ଼ିଆ", ttsTag: "od-IN" },
    { code: "ur", label: "Urdu", nativeLabel: "اردو", ttsTag: "ur-IN" },
    { code: "as", label: "Assamese", nativeLabel: "অসমীয়া", ttsTag: "as-IN" },
    { code: "ne", label: "Nepali", nativeLabel: "नेपाली", ttsTag: "ne-NP" },
    { code: "af", label: "Afrikaans", nativeLabel: "Afrikaans", ttsTag: "af-ZA" },
    { code: "sq", label: "Albanian", nativeLabel: "Shqip", ttsTag: "sq-AL" },
    { code: "ar", label: "Arabic", nativeLabel: "العربية", ttsTag: "ar-SA" },
    { code: "az", label: "Azerbaijani", nativeLabel: "Azərbaycan", ttsTag: "az-AZ" },
    { code: "eu", label: "Basque", nativeLabel: "Euskara", ttsTag: "eu-ES" },
    { code: "be", label: "Belarusian", nativeLabel: "Беларуская", ttsTag: "be-BY" },
    { code: "bs", label: "Bosnian", nativeLabel: "Bosanski", ttsTag: "bs-BA" },
    { code: "bg", label: "Bulgarian", nativeLabel: "Български", ttsTag: "bg-BG" },
    { code: "ca", label: "Catalan", nativeLabel: "Català", ttsTag: "ca-ES" },
    { code: "zh", label: "Chinese", nativeLabel: "中文", ttsTag: "zh-CN" },
    { code: "hr", label: "Croatian", nativeLabel: "Hrvatski", ttsTag: "hr-HR" },
    { code: "cs", label: "Czech", nativeLabel: "Čeština", ttsTag: "cs-CZ" },
    { code: "da", label: "Danish", nativeLabel: "Dansk", ttsTag: "da-DK" },
    { code: "nl", label: "Dutch", nativeLabel: "Nederlands", ttsTag: "nl-NL" },
    { code: "et", label: "Estonian", nativeLabel: "Eesti", ttsTag: "et-EE" },
    { code: "fi", label: "Finnish", nativeLabel: "Suomi", ttsTag: "fi-FI" },
    { code: "fr", label: "French", nativeLabel: "Français", ttsTag: "fr-FR" },
    { code: "gl", label: "Galician", nativeLabel: "Galego", ttsTag: "gl-ES" },
    { code: "de", label: "German", nativeLabel: "Deutsch", ttsTag: "de-DE" },
    { code: "el", label: "Greek", nativeLabel: "Ελληνικά", ttsTag: "el-GR" },
    { code: "he", label: "Hebrew", nativeLabel: "עברית", ttsTag: "he-IL" },
    { code: "hu", label: "Hungarian", nativeLabel: "Magyar", ttsTag: "hu-HU" },
    { code: "id", label: "Indonesian", nativeLabel: "Bahasa Indonesia", ttsTag: "id-ID" },
    { code: "it", label: "Italian", nativeLabel: "Italiano", ttsTag: "it-IT" },
    { code: "ja", label: "Japanese", nativeLabel: "日本語", ttsTag: "ja-JP" },
    { code: "kk", label: "Kazakh", nativeLabel: "Қазақша", ttsTag: "kk-KZ" },
    { code: "ko", label: "Korean", nativeLabel: "한국어", ttsTag: "ko-KR" },
    { code: "lv", label: "Latvian", nativeLabel: "Latviešu", ttsTag: "lv-LV" },
    { code: "lt", label: "Lithuanian", nativeLabel: "Lietuvių", ttsTag: "lt-LT" },
    { code: "mk", label: "Macedonian", nativeLabel: "Македонски", ttsTag: "mk-MK" },
    { code: "ms", label: "Malay", nativeLabel: "Bahasa Melayu", ttsTag: "ms-MY" },
    { code: "no", label: "Norwegian", nativeLabel: "Norsk", ttsTag: "nb-NO" },
    { code: "fa", label: "Persian", nativeLabel: "فارسی", ttsTag: "fa-IR" },
    { code: "pl", label: "Polish", nativeLabel: "Polski", ttsTag: "pl-PL" },
    { code: "pt", label: "Portuguese", nativeLabel: "Português", ttsTag: "pt-BR" },
    { code: "ro", label: "Romanian", nativeLabel: "Română", ttsTag: "ro-RO" },
    { code: "ru", label: "Russian", nativeLabel: "Русский", ttsTag: "ru-RU" },
    { code: "sr", label: "Serbian", nativeLabel: "Српски", ttsTag: "sr-RS" },
    { code: "sk", label: "Slovak", nativeLabel: "Slovenčina", ttsTag: "sk-SK" },
    { code: "sl", label: "Slovenian", nativeLabel: "Slovenščina", ttsTag: "sl-SI" },
    { code: "es", label: "Spanish", nativeLabel: "Español", ttsTag: "es-ES" },
    { code: "sw", label: "Swahili", nativeLabel: "Kiswahili", ttsTag: "sw-KE" },
    { code: "sv", label: "Swedish", nativeLabel: "Svenska", ttsTag: "sv-SE" },
    { code: "tl", label: "Tagalog", nativeLabel: "Tagalog", ttsTag: "tl-PH" },
    { code: "th", label: "Thai", nativeLabel: "ไทย", ttsTag: "th-TH" },
    { code: "tr", label: "Turkish", nativeLabel: "Türkçe", ttsTag: "tr-TR" },
    { code: "uk", label: "Ukrainian", nativeLabel: "Українська", ttsTag: "uk-UA" },
    { code: "vi", label: "Vietnamese", nativeLabel: "Tiếng Việt", ttsTag: "vi-VN" },
    { code: "cy", label: "Welsh", nativeLabel: "Cymraeg", ttsTag: "cy-GB" },
];
const BY_CODE = new Map(LANGUAGES.map((language) => [language.code, language]));
/** The language for a stored code, or null for one no longer in the table. */
export function languageByCode(code) {
    return BY_CODE.get(code) ?? null;
}
/** Human-readable, for a chip or a summary line: "English & Hindi". */
export function describeLanguages(codes) {
    const labels = codes.map((code) => BY_CODE.get(code)?.label ?? code);
    if (labels.length === 0)
        return "—";
    if (labels.length === 1)
        return labels[0];
    return `${labels.slice(0, -1).join(", ")} & ${labels[labels.length - 1]}`;
}
//# sourceMappingURL=languages.js.map